/**
 * ChecklistGroupCard - Cartão de grupo (lista) de checklist
 *
 * Header com título editável, contador, menu ... com ações de grupo.
 * Corpo com itens + barra de progresso + input para adicionar novo item.
 * Suporta drag-and-drop de itens (interno) e de grupos (externo).
 *
 * @version 1.1.0
 * RELEASE: 2026-07-07 v1.0.0 - Criação inicial (Fase 1)
 * RELEASE: 2026-07-07 v1.1.0 - Drag-and-drop de itens + drag handle do grupo
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ChecklistItemRow } from './ChecklistItemRow';
import type { ChecklistGroup } from '@/hooks/useTaskChecklist';
import { cn } from '@/lib/utils';

interface ChecklistGroupCardProps {
  group: ChecklistGroup;
  taskId: string;
  tenantId: string;
  readOnly?: boolean;
  /** Força o estado colapsado (controlado pelo pai). Se omitido, usa estado interno. */
  collapsed?: boolean;
  onRenameGroup: (title: string) => void;
  onDeleteGroup: () => void;
  onBulkToggle: (isDone: boolean) => void;
  onCreateItem: (content: string) => void;
  onUpdateItem: (itemId: string, content: string) => void;
  onToggleItem: (itemId: string, isDone: boolean) => void;
  onDeleteItem: (itemId: string) => void;
  onReorderItems: (itemIds: string[]) => void;
  onUpsertItemComment: (params: {
    itemId: string;
    commentId?: string | null;
    content: string;
    mentionedUserIds: string[];
    attachmentIds?: string[];
    removedAttachmentIds?: string[];
  }) => Promise<string>;
  onDeleteItemComment: (commentId: string) => Promise<unknown>;
}


export function ChecklistGroupCard({
  group,
  taskId,
  tenantId,
  readOnly,
  collapsed: collapsedProp,
  onRenameGroup,
  onDeleteGroup,
  onBulkToggle,
  onCreateItem,
  onUpdateItem,
  onToggleItem,
  onDeleteItem,
  onReorderItems,
  onUpsertItemComment,
  onDeleteItemComment,
}: ChecklistGroupCardProps) {
  // Sortable do grupo (registrado no DndContext do TaskChecklistSection)
  const {
    attributes: groupAttributes,
    listeners: groupListeners,
    setNodeRef: setGroupNodeRef,
    transform: groupTransform,
    transition: groupTransition,
    isDragging: groupIsDragging,
  } = useSortable({
    id: group.id,
    data: { type: 'checklist-group' },
    disabled: readOnly,
  });
  const groupStyle = {
    transform: CSS.Transform.toString(groupTransform),
    transition: groupTransition,
    opacity: groupIsDragging ? 0.6 : 1,
  };

  // DnD interno dos itens do grupo
  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** Manipula fim do drag de item: reordena localmente e persiste */
  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = group.items.findIndex((i) => i.id === active.id);
    const newIndex = group.items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(group.items, oldIndex, newIndex).map((i) => i.id);
    onReorderItems(reordered);
  };

  // Colapsar / expandir grupo — o pai controla o estado GLOBAL (expandir/retrair todos)
  // mas o usuário pode sobrescrever individualmente após isso. Para permitir ambos,
  // sincronizamos o estado interno sempre que o prop `collapsed` mudar, e o toggle
  // individual sempre opera sobre o estado interno.
  const [collapsed, setCollapsed] = useState(collapsedProp ?? false);
  useEffect(() => {
    if (collapsedProp !== undefined) setCollapsed(collapsedProp);
  }, [collapsedProp]);
  const toggleCollapsed = () => setCollapsed((c) => !c);

  // Edição inline do título
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(group.title);
  // Novo item
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);

  // Cálculo de progresso
  const total = group.items.length;
  const done = group.items.filter((i) => i.is_done).length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  /** Persiste rename se mudou */
  const commitTitle = () => {
    const next = titleValue.trim();
    if (next && next !== group.title) onRenameGroup(next);
    else setTitleValue(group.title);
    setEditingTitle(false);
  };

  /** Adiciona novo item se conteúdo válido */
  const submitNewItem = () => {
    const content = newItem.trim();
    if (!content) { setAdding(false); return; }
    onCreateItem(content);
    setNewItem('');
    // Mantém input aberto para adicionar mais itens em sequência
  };

  const itemIds = group.items.map((i) => i.id);

  return (
    <div ref={setGroupNodeRef} style={groupStyle} className="rounded-lg border border-border bg-card">
      {/* Header: colapsar + título + contador + menu */}
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-t-lg">
        {/* Drag handle do grupo */}
        {!readOnly && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground"
            aria-label="Reordenar lista"
            {...groupAttributes}
            {...groupListeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expandir' : 'Recolher'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {editingTitle ? (
          <input
            className="flex-1 bg-transparent outline-none text-sm font-semibold border-b border-primary/40"
            value={titleValue}
            autoFocus
            maxLength={200}
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
              if (e.key === 'Escape') { setTitleValue(group.title); setEditingTitle(false); }
            }}
          />
        ) : (
          <button
            type="button"
            className="flex-1 text-left text-sm font-semibold truncate cursor-text"
            onClick={() => !readOnly && setEditingTitle(true)}
          >
            {group.title}
          </button>
        )}

        <span className="text-xs text-muted-foreground tabular-nums">
          {done}/{total}
        </span>

        {/* Barra de progresso compacta no header */}
        {total > 0 && (() => {
          const progressColor =
            percent >= 100
              ? 'bg-emerald-300'
              : percent >= 34
                ? 'bg-amber-300'
                : 'bg-slate-300';
          const percentColor =
            percent >= 100
              ? 'text-emerald-400 dark:text-emerald-300'
              : percent >= 34
                ? 'text-amber-400 dark:text-amber-300'
                : 'text-slate-400';
          return (
            <div className="hidden sm:flex items-center gap-1.5 w-28 shrink-0">
              <span className={cn('text-[10px] tabular-nums font-medium', percentColor)}>{percent}%</span>
              <Progress value={percent} className="h-1 flex-1" indicatorClassName={cn(progressColor, 'transition-colors')} />
            </div>
          );
        })()}

        {!readOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => onBulkToggle(true)}
                disabled={total === 0 || done === total}
              >
                Marcar todos como concluídos
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => onBulkToggle(false)}
                disabled={done === 0}
              >
                Desmarcar todos
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir lista
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir lista de verificação?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação removerá o grupo <strong>{group.title}</strong> e todos os seus itens.
                      Não é possível desfazer.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={onDeleteGroup}
                    >
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Corpo: itens (sortable) + adicionar + progresso */}
      <div className={cn('transition-all', collapsed && 'hidden')}>
        <div className="p-2 space-y-0.5">
          <DndContext
            sensors={itemSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleItemDragEnd}
          >
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              {group.items.map((item) => (
                <ChecklistItemRow
                  key={item.id}
                  item={item}
                  taskId={taskId}
                  tenantId={tenantId}
                  readOnly={readOnly}
                  onToggle={(isDone) => onToggleItem(item.id, isDone)}
                  onUpdate={(content) => onUpdateItem(item.id, content)}
                  onDelete={() => onDeleteItem(item.id)}
                  onUpsertComment={onUpsertItemComment}
                  onDeleteComment={onDeleteItemComment}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Linha "Adicionar um item" */}
          {!readOnly && (
            adding ? (
              <div className="flex items-center gap-2 py-1.5 px-2">
                <input
                  className="flex-1 bg-transparent outline-none text-sm border-b border-primary/40 pb-0.5"
                  placeholder="Digite o item e pressione Enter"
                  value={newItem}
                  autoFocus
                  maxLength={500}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); submitNewItem(); }
                    if (e.key === 'Escape') { setNewItem(''); setAdding(false); }
                  }}
                  onBlur={() => { if (!newItem.trim()) setAdding(false); }}
                />
              </div>
            ) : (
              <button
                type="button"
                className="flex items-center gap-2 w-full py-1.5 px-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 rounded-md transition-colors"
                onClick={() => setAdding(true)}
              >
                <Plus className="h-4 w-4" />
                Adicionar um item
              </button>
            )
          )}
        </div>

      </div>
    </div>
  );
}
